// @flow
import moment from "moment";
import type Moment from "moment";
import { createSelector } from "reselect";
import {
  any,
  chain,
  complement,
  compose,
  defaultTo,
  filter,
  head,
  indexBy,
  map,
  omit,
  path,
  prop,
  propEq,
  propIs,
  propOr,
  reject,
  sortBy,
  uniq,
  isEmpty,
  values,
} from "ramda";

import { Maybe } from "ramda-fantasy";
import {
  includesIn,
  getObjectKeyByValue,
  isMultiplasMedidasSelecionadas,
} from "../../util";
import { isNotNil } from "@quantum/utils";
import type {
  GrupamentosMedida,
  Id,
  EdicaoMedida,
  ChaveEnum,
  Periodo,
  SelecaoJanela,
  AjusteDias,
  DatasSelecaoJanela,
  EnumIdChaveTraducao,
  EnumCodigoDescricao,
  MedidaSelecionada,
  ModoApp,
} from "../../model";

import {
  isMedidaNaoNumerica,
  parseiaExpressaoPorAtivo,
  parseiaExpressao,
  parseiaExpressaoRanking,
  referenciasDaExpressao,
  GRUPAMENTO_MEDIDAS_QUALITATIVAS,
  GRUPAMENTO_MEDIDAS_PERSONALIZADAS,
  ID_AJUSTE_DIAS_BANCO,
  ID_AJUSTE_DIAS_NORMAL,
  ID_AJUSTE_DIAS_JANELA,
  ID_PERIODICIDADE_DIARIA,
  ID_PERIODICIDADE_SEMANAL,
  ID_PERIODO_DATAS_ESPECIFICAS,
  ID_PERIODO_DATAS_ESPECIFICAS_NAO_MENSAL,
  ID_PERIODO_DATAS_ESPECIFICAS_SERIES,
  ID_PERIODO_MES_A_MES,
  ID_PERIODO_MES_A_MES_MENSAL,
  ID_PERIODO_DATAS_INDICADORES_MERCADO,
  ID_PERIODICIDADE_MENSAL,
  ID_TIPO_JANELA_MESES,
  ID_TIPO_DATA_REFERENCIA_REFERENCIA,
  ID_CONFIGURACAO_RELATORIO_MEDIDA_MEDIDA_USUARIO,
  GRUPAMENTO_MEDIDAS_EMPRESAS,
  IDS_AJUSTE_DIAS_INDICADORES_FINANCEIROS,
  MODOS_EXIBICAO_INDICADORES_FINANCEIROS,
  MAP_ID_AJUSTE_DIAS_TIPO_DATA_REFERENCIA_INDICADORES_FINANCEIROS,
  MODO_EXIBICAO_ACUMULACAO_DEFLATOR_COM_ANALISE_HORIZONTAL,
  ID_PERIODO_ANALISE_HORIZONTAL,
  ID_TIPO_CALCULO_PONTUAL,
  ID_TIPO_CALCULO_VARIACAO_PERCENTUAL,
  MODOS_EXIBICAO_INDICADORES_MERCADO,
  MAP_ID_TIPO_QUANTIDADE_DIAS_REFERENCIA_POR_PERIODO,
  GRUPAMENTO_MEDIDAS_QUANTITATIVAS,
  GRUPAMENTO_MEDIDAS_SERIES,
  ID_PERIODO_INICIO_DA_SERIE,
  ID_PERIODO_FINAL_DA_SERIE,
  ID_PERIODO_NO_DIA,
  MODO_EXIBICAO_PONTUAL_MENSAL,
  TIPO_LISTA_PERIODO_PERIODO_DIARIO,
  TIPO_LISTA_PERIODO_DEFAULT,
  MODO_EXIBICAO_PONTUAL_MENSAL_ATIVO_COMPRADO,
  MODO_EXIBICAO_PONTUAL_MENSAL_COMPRADOR,
  MODO_EXIBICAO_PONTUAL_MENSAL_CARTEIRA_EM_NIVEIS,
  TIPO_VISUALIZACAO_CARTEIRA_INDIVIDUAL,
  TIPO_RETORNO_NUMERADOR_MEDIO,
  TIPO_CALCULO_NUMERADOR_ARITMETICO,
  PERIODICIDADE_ESTATISTICA_ANUAL,
  TIPO_VOLATILIDADE_ATIVO,
  ESTIMACAO_VOLATILIDADE_AMOSTRA,
  TIPO_RETORNO_ESTATISTICA_DISCRETO,
  MODO_APP_SERIES,
  FORMATO_DATA_BANCO,
  MODO_EXIBICAO_RANKING,
  MODO_EXIBICAO_MEDIDA_COMPOSTA,
  MEDIDAS_SEM_CASAS_DECIMAIS,
  FORMATACAO_MOEDAS,
  FORMATACAO_MOEDA_MILHARES,
} from "../../model";
import {
  type SimpleSelector,
  type State,
  type MapEdicaoMedida,
  getMedidaSelecionadaEdicao,
  getMedidasPorGrupo,
} from "../";

import {
  getAjusteDias,
  getAnoBaseEmpresasAbertas,
  getMedidaPorId,
  getModoExibicaoPorChave,
  getPeriodosPorTipoLista,
  getPeriodoPorId,
  getTiposDataReferencia,
  getTiposJanela,
  getTrimestreBaseEmpresasAbertas,
  getMedidasPorId,
} from "./dominioSelectors";
import { getModoApp } from "./comum";
import { getDataBaseSelecionada } from "./dataBaseSelectors";
import { getGrupamentoMedidaSelecionado } from "./filtroMedidasSelectors";
import {
  getCamposPorMedida,
  getIdFuncaoRankingPorFuncao,
} from "./dominioSelectors";

type Selector<A> = State => A;

const getMapEdicaoMedida = (state: State): * => state.edicaoMedida;

const getEdicaoMedidaLocal: Selector<EdicaoMedida> = createSelector(
  getGrupamentoMedidaSelecionado,
  getMapEdicaoMedida,
  (grupamento: GrupamentosMedida, edicaoMedida: MapEdicaoMedida) =>
    edicaoMedida[grupamento]
);

export const getEdicaoMedidaGlobal: Selector<EdicaoMedida> = createSelector(
  getMapEdicaoMedida,
  (edicaoMedida: MapEdicaoMedida) => edicaoMedida.GLOBAL
);

export const getEdicaoMedida: State => EdicaoMedida = createSelector(
  getModoApp,
  getEdicaoMedidaGlobal,
  getEdicaoMedidaLocal,
  (modoApp: ModoApp, edicaoGlobal: EdicaoMedida, edicaoLocal: EdicaoMedida) => {
    switch (modoApp) {
      case "SERIES":
        return {
          ...edicaoLocal,
          periodos: edicaoGlobal.periodos,
          periodicidade: edicaoGlobal.periodicidade,
          ajusteDias: edicaoGlobal.ajusteDias,
          dataInicial: edicaoGlobal.dataInicial,
          dataFinal: edicaoGlobal.dataFinal,
        };

      default:
        return edicaoLocal;
    }
  }
);

export const getPeriodosSelecionados = (state: State): ?Array<Id> =>
  getEdicaoMedida(state).periodos;

export const getIdMedidaSelecionada = (state: State): ?Id =>
  getEdicaoMedida(state).medida;

export const getIdMedidaUsuarioSelecionada = (state: State): Array<Id> =>
  getEdicaoMedida(state).idMedidaUsuario || [];

export const getGrupamentoMedidaSelecionada = (state: State): ?string => {
  const medidaSelecionada = getMedidaSelecionada(state);

  if (medidaSelecionada) {
    return isMultiplasMedidasSelecionadas(medidaSelecionada)
      ? medidaSelecionada[0].grupamentoMedida.nome //REFATORAR
      : medidaSelecionada.grupamentoMedida.nome;
  }
  return null;
};

export const getMedidaSelecionada = (state: State) => {
  const ids = Maybe(getIdMedidaSelecionada(state));

  return ids.value &&
    Array.isArray(ids.value) &&
    ids.value.length > 1 &&
    state.grupamentoMedidaSelecionado !== GRUPAMENTO_MEDIDAS_QUALITATIVAS
    ? ids.map(getMedidasPorId(state)).getOrElse(null)
    : ids.map(getMedidaPorId(state)).getOrElse(null);
};

export const getPeriodicidadeSelecionada = (state: State): ?Id =>
  getEdicaoMedida(state).periodicidade;

export const getAjusteDiasSelecionado = (state: State): ?Id =>
  getEdicaoMedida(state).ajusteDias;

export const getTipoContaSelecionado = (state: State): ?Id =>
  getEdicaoMedida(state).tipoConta;

export const getVersaoContaSelecionada = (state: State): ?Id =>
  getEdicaoMedida(state).versaoConta;

export const getFormaAcumulacaoSelecionada = (state: State): ?Id =>
  getEdicaoMedida(state).formaAcumulacao;

export const getToleranciaDFSelecionada = (state: State): ?Id =>
  getEdicaoMedida(state).toleranciaDF;

export const getCapitalUtilizadoSelecionado = (state: State): ?Id =>
  getEdicaoMedida(state).capitalUtilizado;

export const getAnosSelecionados = (state: State): ?Array<Id> =>
  getEdicaoMedida(state).anos;

export const getTipoReferenciaSelecionado = (state: State): ?Id =>
  getEdicaoMedida(state).tipoReferencia;

export const getTrimestreReferenciaSelecionado = (state: State): ?Id =>
  getEdicaoMedida(state).trimestreReferencia;

export const getComparacaoSelecionada = (state: State): ?Id =>
  getEdicaoMedida(state).comparacao;

export const getLambdaSelecionado = (state: State): ?number =>
  getEdicaoMedida(state).lambda;

export const getAtivoSemRiscoSelecionado = (state: State): ?string =>
  getEdicaoMedida(state).ativoSemRisco;

export const getTaxaDeAdministracaoSelecionada = (state: State): ?Id =>
  getEdicaoMedida(state).taxaDeAdministracao;

export const getHorizonteDuSelecionado = (state: State): ?number =>
  getEdicaoMedida(state).horizonte;

export const getConfiancaSelecionada = (state: State): ?number =>
  getEdicaoMedida(state).confianca;

export const getVolatilidadeSelecionada = (state: State): ?number =>
  getEdicaoMedida(state).volatilidade;

export const getTipoDoRetornoSelecionado = (state: State): ?number =>
  getEdicaoMedida(state).tipoDoRetorno;

export const getBenchmarkSelecionado = (state: State): ?string =>
  getEdicaoMedida(state).benchmark;

export const getCompetenciaSelecionada = (state: State): ?string =>
  getEdicaoMedida(state).competencia;

export const getIndiceSelecionado = (state: State): ?string =>
  getEdicaoMedida(state).indice;

export const getLimiteSelecionado = (state: State): ?number =>
  getEdicaoMedida(state).limite;

export const getUsarUltimaNegociacaoMes = (state: State): ?boolean =>
  getEdicaoMedida(state).ultimaNegociacaoMes;

type IndicesSelecionadosSelector = State => Array<string>;
export const getIndicesSelecionados: IndicesSelecionadosSelector = createSelector(
  getEdicaoMedida,
  propOr([], "indices")
);

export const getFrequenciasSelecionadas = (state: State): ?Array<Id> =>
  getEdicaoMedida(state).frequencias;

export const getAjustesPrecoSelecionados = (state: State): ?Array<Id> =>
  getEdicaoMedida(state).ajustesPreco;

export const getFonteFidcSelecionada = (state: State): ?Id =>
  getEdicaoMedida(state).fonteFidc;

export const getJanelaMovelSelecionada = (state: State): ?number =>
  getEdicaoMedida(state).janelaMovel;

export const getExibirConfiguracoesAvancadas = (state: State): ?boolean =>
  getEdicaoMedida(state).exibirConfiguracoesAvancadas;

export const getPlUtilizadoSelecionado = (state: State): ?Id =>
  getEdicaoMedida(state).plUtilizado;

export const getDataEspecificaSelecionada = (state: State): ?Moment =>
  getEdicaoMedida(state).dataEspecifica;

export const getToleranciaDiasValorMercadoSelecionada = (
  state: State
): ?number => getEdicaoMedida(state).toleranciaDiasValorMercado;

export const getDeflatorSelecionado = (state: State): ?number =>
  getEdicaoMedida(state).deflator;

export const getEscalaMoedaSelecionada = (state: State): ?Id =>
  getEdicaoMedida(state).escala;

export const getValorBaseDeflatorSelecionado = (state: State): ?Id =>
  getEdicaoMedida(state).valorBaseDeflator;

export const getValorBaseDeflatorOutroSelecionado = (state: State): ?number =>
  getEdicaoMedida(state).valorBaseDeflatorOutro;

export const getDataBaseDeflatorSelecionado = (state: State): ?Id =>
  getEdicaoMedida(state).dataBaseDeflator;

export const getDataBaseDeflatorEspecificaSelecionada = (
  state: State
): ?Moment => getEdicaoMedida(state).dataBaseDeflatorDataEspecifica;

export const getDataInicialCompostaSelecionada = (state: State): ?string =>
  getEdicaoMedida(state).dataInicialComposta;

export const getDataFinalCompostaSelecionada = (state: State): ?string =>
  getEdicaoMedida(state).dataFinalComposta;

export const getDataInicialSelecionada = (state: State): ?Moment =>
  getEdicaoMedida(state).dataInicial;

export const getDataInicialFormatadaBanco = (state: State): ?string => {
  const dataInicialSelecionada = getDataInicialSelecionada(state);
  return dataInicialSelecionada
    ? dataInicialSelecionada.format(FORMATO_DATA_BANCO)
    : null;
};

export const getDataFinalSelecionada = (state: State): ?Moment =>
  getEdicaoMedida(state).dataFinal;

export const getDataFinalFormatadaBanco = (state: State): ?string => {
  const dataFinalSelecionada = getDataFinalSelecionada(state);
  return dataFinalSelecionada
    ? dataFinalSelecionada.format(FORMATO_DATA_BANCO)
    : null;
};

export const getDataInicialEFinalSelecionada: State => Array<?Moment> = createSelector(
  getDataInicialSelecionada,
  getDataFinalSelecionada,
  (inicial, final) => [inicial, final]
);

export const isPeriodoUnicoSelecionado = (state: State): boolean =>
  (getPeriodosSelecionados(state) || []).length === 1;

export const getPeriodoUnicoSelecionado = (state: State): ?Id =>
  isPeriodoUnicoSelecionado(state)
    ? (getPeriodosSelecionados(state) || [])[0]
    : undefined;

export const getDatasUteisSelecaoJanela = (
  state: State
): ?Array<DatasSelecaoJanela> => getEdicaoMedida(state).datasUteisSelecaoJanela;

export const getMesesSelecaoJanela = (
  state: State
): ?Array<DatasSelecaoJanela> => getEdicaoMedida(state).mesesSelecaoJanela;

export const getSelecaoJanela = (state: State): SelecaoJanela => {
  return {
    tamanhoJanela: getTamanhoJanelaSelecionada(state),
    quantidadeJanelas: getQuantidadeJanelasSelecionada(state),
    tipoJanela: getTipoJanelaSelecionada(state),
    ajusteDias: getAjusteDiasSelecionado(state),
    dataBase: getDataBaseSelecionada(state),
    periodicidade: getPeriodicidadeSelecionada(state),
  };
};

export const getTamanhoJanelaSelecionada = (state: State): ?number =>
  getEdicaoMedida(state).tamanhoJanela;

export const getQuantidadeJanelasSelecionada = (state: State): ?number =>
  getEdicaoMedida(state).quantidadeJanelas;

export const getTipoJanelaSelecionada = (state: State): ?Id =>
  getEdicaoMedida(state).tipoJanela;

export const getValorComboMedidas = (state: State): ?string =>
  getEdicaoMedida(state).valorComboMedidas;

export const getIndiceMedidaEditada = (state: State): ?number =>
  getEdicaoMedida(state).indiceMedidaEditada;

export const isMedidaPontualSelecionada = (state: State): boolean => {
  const medidaSelecionada = getMedidaSelecionada(state);

  if (medidaSelecionada && !isMultiplasMedidasSelecionadas(medidaSelecionada)) {
    return medidaSelecionada.modoExibicao.includes("PONTUAL");
  }
  return false;
};

export const getTipoCalculoSelecionado = (state: State): ?Id => {
  return getEdicaoMedida(state).tipoCalculo;
};

export const isMedidaQualiSelecionada = (state: State): boolean => {
  const isGrupamentoSelecionado =
    getGrupamentoMedidaSelecionado(state) === GRUPAMENTO_MEDIDAS_QUALITATIVAS;
  const medidasSelecionadas = getIdMedidaSelecionada(state) || [];
  return isGrupamentoSelecionado && !isEmpty(medidasSelecionadas);
};

export const getIsIndicadorFinanceiroSelecionado = (state: State): ?boolean => {
  const medidaSelecionada = getMedidaSelecionada(state);
  return (
    medidaSelecionada &&
    !isMultiplasMedidasSelecionadas(medidaSelecionada) &&
    MODOS_EXIBICAO_INDICADORES_FINANCEIROS.includes(
      medidaSelecionada.modoExibicao
    )
  );
};

export const getModoExibicaoMedidaSelecionada = (state: State) => {
  const medidaSelecionada = getMedidaSelecionada(state);
  const modoApp: ?ModoApp = getModoApp(state);

  if (medidaSelecionada) {
    if (modoApp === MODO_APP_SERIES) {
      return isMultiplasMedidasSelecionadas(medidaSelecionada)
        ? medidaSelecionada.map(medida =>
            Maybe(medida.modoExibicaoModoSeries).getOrElse(medida.modoExibicao)
          )
        : Maybe(medidaSelecionada.modoExibicaoModoSeries).getOrElse(
            medidaSelecionada.modoExibicao
          );
    }
    if (!isMultiplasMedidasSelecionadas(medidaSelecionada)) {
      return medidaSelecionada.modoExibicao;
    }
  }
  return null;
};

export const getTiposDataReferenciaAnaliseHorizontal = (
  state: State
): Array<EnumCodigoDescricao> => {
  const tiposDataReferencia: Array<EnumCodigoDescricao> = getTiposDataReferencia(
    state
  );

  return tiposDataReferencia.filter(
    tipo => tipo.codigo !== ID_TIPO_DATA_REFERENCIA_REFERENCIA
  );
};

export const isSomenteAnoETrimestreBaseEmpresasAbertasSelecionados = (
  state: State
): ?boolean => {
  const anoBase: ?number = getAnoBaseEmpresasAbertas(state);
  const trimestreBase: ?number = getTrimestreBaseEmpresasAbertas(state);
  const anosSelecionados: ?Array<number> = getAnosSelecionados(state);
  const frequenciasSelecionadas: ?Array<Id> = getFrequenciasSelecionadas(state);

  return (
    anosSelecionados &&
    anosSelecionados.length === 1 &&
    anosSelecionados.includes(anoBase) &&
    frequenciasSelecionadas &&
    frequenciasSelecionadas.length === 1 &&
    frequenciasSelecionadas.includes(trimestreBase)
  );
};

export const getIsIndicadorDeMercadoSelecionado = (state: State): boolean =>
  MODOS_EXIBICAO_INDICADORES_MERCADO.includes(
    getModoExibicaoMedidaSelecionada(state)
  );

export const getIsPeriodoInicioDaSerieSelecionado = (
  state: State
): ?boolean => {
  const periodosSelecionados: ?Array<Id> = getPeriodosSelecionados(state);

  return (
    periodosSelecionados &&
    periodosSelecionados.includes(ID_PERIODO_INICIO_DA_SERIE)
  );
};

export const getIsPeriodoFinalDaSerieSelecionado = (state: State): ?boolean => {
  const periodosSelecionados: ?Array<Id> = getPeriodosSelecionados(state);

  return (
    periodosSelecionados &&
    periodosSelecionados.includes(ID_PERIODO_FINAL_DA_SERIE)
  );
};

export const getIsPeriodoInicioOuFinalDaSerieSelecionado = (
  state: State
): ?boolean => {
  return (
    getIsPeriodoInicioDaSerieSelecionado(state) ||
    getIsPeriodoFinalDaSerieSelecionado(state)
  );
};

export const getIsPeriodoNoDiaSelecionado = (state: State): ?boolean => {
  const periodosSelecionados: ?Array<Id> = getPeriodosSelecionados(state);

  return (
    periodosSelecionados && periodosSelecionados.includes(ID_PERIODO_NO_DIA)
  );
};

export const getListaPeriodosVisiveis = (state: State): Array<Periodo> => {
  const medidaSelecionada = getMedidaSelecionada(state);
  const modoExibicaoMedidaSelecionada: ?ChaveEnum = !isMultiplasMedidasSelecionadas(
    medidaSelecionada
  )
    ? medidaSelecionada && medidaSelecionada.modoExibicao
    : null;
  const tipoListaPeriodo =
    modoExibicaoMedidaSelecionada &&
    getModoExibicaoPorChave(state)(modoExibicaoMedidaSelecionada)
      .tipoListaPeriodo;
  const periodicidadeSelecionada: ?Id = getPeriodicidadeSelecionada(state);

  if (modoExibicaoMedidaSelecionada != null && tipoListaPeriodo) {
    const periodos: Array<Periodo> = getPeriodosPorTipoLista(state)[
      tipoListaPeriodo
    ].map(idPeriodo => getPeriodoPorId(state)(idPeriodo));

    const _periodos: Array<Periodo> = periodos.filter(periodo =>
      typeof periodicidadeSelecionada === "number"
        ? periodo.periodicidades.includes(periodicidadeSelecionada)
        : true
    );

    if (modoExibicaoMedidaSelecionada === MODO_EXIBICAO_PONTUAL_MENSAL) {
      const mesAMes: Periodo = getPeriodoPorId(state)(ID_PERIODO_MES_A_MES);
      if (!_periodos.some(periodo => periodo.id === ID_PERIODO_MES_A_MES)) {
        _periodos.unshift(mesAMes);
      }
    }

    if (
      periodicidadeSelecionada === ID_PERIODICIDADE_MENSAL &&
      (tipoListaPeriodo === TIPO_LISTA_PERIODO_DEFAULT ||
        tipoListaPeriodo === TIPO_LISTA_PERIODO_PERIODO_DIARIO)
    ) {
      return _periodos.filter(periodo => periodo.id !== ID_PERIODO_MES_A_MES);
    }

    return _periodos;
  } else {
    return [];
  }
};

export const getExibirSelecaoDatasEspecificas = (state: State): boolean => {
  const modoApp = getModoApp(state);
  const periodoUnicoSelecionado = getPeriodoUnicoSelecionado(state);

  switch (modoApp) {
    case "SERIES":
      return periodoUnicoSelecionado === ID_PERIODO_DATAS_ESPECIFICAS_SERIES;

    default:
      return (
        periodoUnicoSelecionado === ID_PERIODO_DATAS_ESPECIFICAS ||
        periodoUnicoSelecionado === ID_PERIODO_DATAS_ESPECIFICAS_NAO_MENSAL
      );
  }
};

function getAjusteDiasAExibirNormal(state: State): Array<AjusteDias> {
  const listaAjusteDias: Array<AjusteDias> = getAjusteDias(state);
  const grupamentoMedidaSelecionada: ?ChaveEnum = getGrupamentoMedidaSelecionada(
    state
  );
  const periodoUnicoSelecionado: ?Id = getPeriodoUnicoSelecionado(state);
  const periodicidadeSelecionada: ?Id = getPeriodicidadeSelecionada(state);
  const somenteAnoETrimestreBaseSelecionados: ?boolean = isSomenteAnoETrimestreBaseEmpresasAbertasSelecionados(
    state
  );
  const isIndicadorDeMercadoSelecionado: boolean = getIsIndicadorDeMercadoSelecionado(
    state
  );
  const isIndicadorFinanceiroSelecionado: ?boolean = getIsIndicadorFinanceiroSelecionado(
    state
  );
  const modoExibicaoMedidaSelecionada:
    | ?ChaveEnum
    | ?Array<ChaveEnum> = getModoExibicaoMedidaSelecionada(state);
  const tipoCalculoSelecionado = getTipoCalculoSelecionado(state);
  let _listaAjusteDias: Array<AjusteDias> = [];

  // A ordem em que os ajustes aparecem é importante
  if (!isIndicadorDeMercadoSelecionado) {
    _listaAjusteDias = [
      ..._listaAjusteDias,
      ...listaAjusteDias.filter(ajuste => ajuste.id === ID_AJUSTE_DIAS_NORMAL),
    ];
  }

  // O ajuste de dias Banco aparece pra todos os grupamentos diferente do empresas sempre
  // Para o grupamento empresa ele aparece se um indicador financeiro está selecionado
  // e se somente o ano e trimestre base estão selecionados
  // ou se um indicador de mercado está selecionado
  if (
    (grupamentoMedidaSelecionada === GRUPAMENTO_MEDIDAS_EMPRESAS &&
      ((isIndicadorFinanceiroSelecionado &&
        somenteAnoETrimestreBaseSelecionados) ||
        isIndicadorDeMercadoSelecionado)) ||
    grupamentoMedidaSelecionada !== GRUPAMENTO_MEDIDAS_EMPRESAS
  ) {
    _listaAjusteDias = [
      ..._listaAjusteDias,
      ...listaAjusteDias.filter(ajuste => ajuste.id === ID_AJUSTE_DIAS_BANCO),
    ];
  }

  if (
    periodoUnicoSelecionado === ID_PERIODO_MES_A_MES ||
    periodoUnicoSelecionado === ID_PERIODO_MES_A_MES_MENSAL
  ) {
    _listaAjusteDias = [
      ..._listaAjusteDias,
      ...listaAjusteDias.filter(ajuste => ajuste.id === ID_AJUSTE_DIAS_JANELA),
    ];
  }

  if (
    ([GRUPAMENTO_MEDIDAS_QUANTITATIVAS, GRUPAMENTO_MEDIDAS_SERIES].includes(
      grupamentoMedidaSelecionada
    ) &&
      periodicidadeSelecionada === ID_PERIODICIDADE_DIARIA &&
      modoExibicaoMedidaSelecionada !== MODO_EXIBICAO_PONTUAL_MENSAL &&
      modoExibicaoMedidaSelecionada !==
        MODO_EXIBICAO_PONTUAL_MENSAL_ATIVO_COMPRADO &&
      modoExibicaoMedidaSelecionada !==
        MODO_EXIBICAO_PONTUAL_MENSAL_COMPRADOR &&
      modoExibicaoMedidaSelecionada !==
        MODO_EXIBICAO_PONTUAL_MENSAL_CARTEIRA_EM_NIVEIS) ||
    (grupamentoMedidaSelecionada === GRUPAMENTO_MEDIDAS_EMPRESAS &&
      isIndicadorDeMercadoSelecionado)
  ) {
    _listaAjusteDias = [
      ..._listaAjusteDias,
      ...listaAjusteDias.filter(ajuste => ajuste.chaveCalculo.includes("dia")),
    ];
  }

  if (
    grupamentoMedidaSelecionada === GRUPAMENTO_MEDIDAS_EMPRESAS &&
    isIndicadorFinanceiroSelecionado &&
    somenteAnoETrimestreBaseSelecionados &&
    (modoExibicaoMedidaSelecionada !==
      MODO_EXIBICAO_ACUMULACAO_DEFLATOR_COM_ANALISE_HORIZONTAL ||
      (modoExibicaoMedidaSelecionada ===
        MODO_EXIBICAO_ACUMULACAO_DEFLATOR_COM_ANALISE_HORIZONTAL &&
        tipoCalculoSelecionado === ID_TIPO_CALCULO_PONTUAL))
  ) {
    _listaAjusteDias = [
      ..._listaAjusteDias,
      ...listaAjusteDias.filter(ajuste =>
        IDS_AJUSTE_DIAS_INDICADORES_FINANCEIROS.includes(ajuste.id)
      ),
    ];
  }

  return _listaAjusteDias;
}

const getAjusteDiasAExibirSeries: State => Array<AjusteDias> = createSelector(
  getAjusteDias,
  getPeriodosSelecionados,
  getPeriodicidadeSelecionada,
  (ajusteDias: Array<AjusteDias>, periodos: ?Array<Id>, periodicidade: ?Id) => {
    const periodicidadeSemanal = periodicidade === ID_PERIODICIDADE_SEMANAL;
    const periodicidadeDiaria = periodicidade === ID_PERIODICIDADE_DIARIA;
    const periodoNoDia = periodos && periodos.includes(ID_PERIODO_NO_DIA);

    return periodicidadeDiaria || periodicidadeSemanal || periodoNoDia
      ? ajusteDias
      : [
          ...ajusteDias.filter(ajuste =>
            [ID_AJUSTE_DIAS_NORMAL, ID_AJUSTE_DIAS_BANCO].includes(ajuste.id)
          ),
        ];
  }
);

export const getAjusteDiasAExibir: State => Array<AjusteDias> = createSelector(
  getModoApp,
  getAjusteDiasAExibirNormal,
  getAjusteDiasAExibirSeries,
  (modo, normal, series) => (modo === MODO_APP_SERIES ? series : normal)
);

export function getTiposJanelaAExibir(
  state: State
): Array<EnumIdChaveTraducao> {
  const tiposJanela: Array<EnumIdChaveTraducao> = getTiposJanela(state);
  const periodicidadeSelecionada: ?Id = getPeriodicidadeSelecionada(state);

  return periodicidadeSelecionada &&
    periodicidadeSelecionada === ID_PERIODICIDADE_MENSAL
    ? tiposJanela.filter(tipo => tipo.id === ID_TIPO_JANELA_MESES)
    : tiposJanela;
}

const gerarCamposEdicaoMedidaPersonalizada = (
  state: State,
  medidaSelecionada: MedidaSelecionada
) => {
  switch (medidaSelecionada.medida.modoExibicao) {
    case MODO_EXIBICAO_RANKING: {
      const { funcao, pesos } = Maybe(medidaSelecionada.expressao)
        .map(parseiaExpressaoRanking)
        .chain(Maybe)
        .getOrElse({ funcao: "ranking", pesos: {} });

      return Maybe(getIdFuncaoRankingPorFuncao(funcao, state))
        .map(funcao => ({
          colunasRanking: [funcao],
          pesosRanking: pesos,
        }))
        .getOrElse({});
    }

    case MODO_EXIBICAO_MEDIDA_COMPOSTA:
      return {
        nome: medidaSelecionada.label,
        expressao: medidaSelecionada.expressao,
        formato: medidaSelecionada.formato,
      };

    default:
      return {};
  }
};

export function gerarEdicaoMedida(
  state: State,
  indiceMedida: number
): EdicaoMedida {
  const medidaSelecionada = getMedidaSelecionadaEdicao(state, indiceMedida);
  let periodos: Array<Id>;
  if (
    MODOS_EXIBICAO_INDICADORES_MERCADO.includes(
      medidaSelecionada.medida.modoExibicao
    )
  ) {
    const idPeriodo =
      medidaSelecionada.periodo === ID_PERIODO_DATAS_INDICADORES_MERCADO
        ? getObjectKeyByValue(
            MAP_ID_TIPO_QUANTIDADE_DIAS_REFERENCIA_POR_PERIODO,
            medidaSelecionada.tipoQuantidadeDiasReferencia
          )
        : medidaSelecionada.periodo;
    periodos = [parseInt(idPeriodo, 10)];
  } else if (
    medidaSelecionada.medida.modoExibicao ===
      MODO_EXIBICAO_PONTUAL_MENSAL_COMPRADOR ||
    medidaSelecionada.medida.modoExibicao ===
      MODO_EXIBICAO_PONTUAL_MENSAL_ATIVO_COMPRADO
  ) {
    periodos = Maybe(medidaSelecionada.idGrupoMedidasSelecionadas)
      .chain(grupo => Maybe(getMedidasPorGrupo(state)[grupo]))
      .map(chain(prop("periodo")))
      .map(uniq)
      .getOrElse([]);
  } else {
    periodos =
      typeof medidaSelecionada.periodo === "number"
        ? [medidaSelecionada.periodo]
        : [];
  }

  const modoApp = getModoApp(state);

  const ativosComprados = Maybe(medidaSelecionada.tipoAtivoComprado)
    .map(() => medidaSelecionada)
    .map(
      ({
        idAtivoComprado: id,
        nomeAtivoComprado: nome,
        tipoAtivoComprado: tipoItemSelecionavel,
      }) =>
        id && nome && tipoItemSelecionavel
          ? [{ id, nome, tipoItemSelecionavel }]
          : []
    )
    .getOrElse([]);

  // Aqui detectamos se o usuário está editando uma medida agregada, e quando estiver
  // iremos popular o campo `campos` com a medida secundária original
  const campos = Maybe(medidaSelecionada.idGrupoMedidasSelecionadas)
    .chain(grupo => Maybe(getMedidasPorGrupo(state)[grupo]))
    .map(map(path(["medida", "codigoOriginal"])))
    .map(uniq)
    .getOrElse([]);

  const casasDecimais = isNotNil(medidaSelecionada.casasDecimais)
    ? medidaSelecionada.casasDecimais
    : Maybe(medidaSelecionada.idGrupoMedidasSelecionadas)
        .chain(grupo => Maybe(getMedidasPorGrupo(state)[grupo]))
        .map(map(propOr(null, "casasDecimais")))
        .map(filter(isNotNil))
        .map(head)
        .map(defaultTo(null))
        .getOrElse(null);

  const escala = isNotNil(medidaSelecionada.escala)
    ? medidaSelecionada.escala
    : Maybe(medidaSelecionada.idGrupoMedidasSelecionadas)
        .chain(grupo => Maybe(getMedidasPorGrupo(state)[grupo]))
        .map(map(propOr(null, "escala")))
        .map(filter(isNotNil))
        .map(head)
        .map(defaultTo(null))
        .getOrElse(null);

  const ativos = Maybe(medidaSelecionada.idGrupoMedidasSelecionadas)
    .chain(grupo => Maybe(getMedidasPorGrupo(state)[grupo]))
    .map(
      chain(({ idAtivoComprado: id, nomeAtivoComprado: nome }) =>
        id && nome ? [{ id, nome }] : []
      )
    )
    .map(uniq)
    .getOrElse(ativosComprados);

  // Selecionaremos o maior rank comprador do grupo
  const compradores = Maybe(medidaSelecionada.idGrupoMedidasSelecionadas)
    .chain(grupo => Maybe(getMedidasPorGrupo(state)[grupo]))
    .map(map(prop("rankComprador")))
    .map(filter(rank => typeof rank === "number"))
    .chain(ranks =>
      ranks.length > 0 ? Maybe(Math.max(...ranks)) : Maybe.Nothing()
    )
    .getOrElse(undefined);

  const indices = Maybe(medidaSelecionada.idGrupoMedidasSelecionadas)
    .chain(grupo => Maybe(getMedidasPorGrupo(state)[grupo]))
    .map(map(prop("indice")))
    .map(uniq)
    .getOrElse(
      Maybe(medidaSelecionada.indice)
        .map(indice => [indice])
        .getOrElse(undefined)
    );

  const idMedidaUsuario = Maybe(medidaSelecionada.idMedidaUsuario)
    .chain(grupo => Maybe(getMedidasPorGrupo(state)[grupo]))
    .map(map(prop("idMedidaUsuario")))
    .map(uniq)
    .getOrElse(
      Maybe(medidaSelecionada.idMedidaUsuario)
        .map(idMedidaUsuario => [idMedidaUsuario])
        .getOrElse(undefined)
    );

  const edicaoMedida: EdicaoMedida = {
    indiceMedidaEditada: indiceMedida,
    idGrupoMedidasSelecionadas: medidaSelecionada.idGrupoMedidasSelecionadas,
    medida:
      medidaSelecionada.medida.grupamentoMedida.nome ===
      GRUPAMENTO_MEDIDAS_QUALITATIVAS
        ? // $FlowFixMe
          [medidaSelecionada.medida.idConfiguracaoRelatorio]
        : medidaSelecionada.medida.idConfiguracaoRelatorio,
    alias: medidaSelecionada.alias,
    periodicidade: medidaSelecionada.periodicidade,
    valorComboMedidas: medidaSelecionada.medida.nome,
    ativoSemRisco: medidaSelecionada.ativoSemRisco,
    benchmark: medidaSelecionada.benchmark,
    capitalUtilizado: medidaSelecionada.capitalUtilizado,
    competencia: medidaSelecionada.competencia,
    confianca: medidaSelecionada.confianca && medidaSelecionada.confianca * 100,
    dataBaseDeflator: medidaSelecionada.dataBaseDeflator,
    dataBaseDeflatorDataEspecifica:
      medidaSelecionada.dataBaseDeflatorDataEspecifica,
    dataFinalComposta: medidaSelecionada.dataFinalComposta,
    dataInicialComposta: medidaSelecionada.dataInicialComposta,
    deflator: medidaSelecionada.deflator,
    exibirConfiguracoesAvancadas: true,
    formaAcumulacao: medidaSelecionada.formaAcumulacao,
    horizonte: medidaSelecionada.horizonte,
    indice: medidaSelecionada.indice,
    indices,
    lambda: medidaSelecionada.lambda,
    plUtilizado: medidaSelecionada.plUtilizado,
    quantidadeJanelas: medidaSelecionada.quantidadeJanelas,
    tamanhoJanela: medidaSelecionada.tamanhoJanela,
    taxaDeAdministracao: medidaSelecionada.taxaDeAdministracao,
    tipoConta: medidaSelecionada.tipoConta,
    tipoDoRetorno: medidaSelecionada.tipoDoRetorno,
    tipoJanela: medidaSelecionada.tipoJanela,
    tipoReferencia: medidaSelecionada.tipoReferencia,
    toleranciaDF: medidaSelecionada.toleranciaDF,
    toleranciaDiasValorMercado: medidaSelecionada.toleranciaDiasValorMercado,
    trimestreReferencia: medidaSelecionada.trimestreReferencia,
    valorBaseDeflator: medidaSelecionada.valorBaseDeflator,
    valorBaseDeflatorOutro: medidaSelecionada.valorBaseDeflatorOutro,
    versaoConta: medidaSelecionada.versaoConta,
    volatilidade: medidaSelecionada.volatilidade,
    comparacao: medidaSelecionada.dataReferencia,
    visualizacaoCarteira:
      medidaSelecionada.visualizacaoCarteira ||
      TIPO_VISUALIZACAO_CARTEIRA_INDIVIDUAL,
    limite: medidaSelecionada.limite,
    retornoNumerador:
      medidaSelecionada.retornoNumerador || TIPO_RETORNO_NUMERADOR_MEDIO,
    calculoNumerador:
      medidaSelecionada.calculoNumerador || TIPO_CALCULO_NUMERADOR_ARITMETICO,
    periodicidadeNumerador:
      medidaSelecionada.periodicidadeNumerador ||
      PERIODICIDADE_ESTATISTICA_ANUAL,
    tipoVolatilidade:
      medidaSelecionada.tipoVolatilidade || TIPO_VOLATILIDADE_ATIVO,
    estimacaoVolatilidade:
      medidaSelecionada.estimacaoVolatilidade || ESTIMACAO_VOLATILIDADE_AMOSTRA,
    periodicidadeVolatilidade:
      medidaSelecionada.periodicidadeVolatilidade ||
      PERIODICIDADE_ESTATISTICA_ANUAL,
    tipoRetornoConfiguracoesEstatistica:
      medidaSelecionada.tipoRetornoConfiguracoesEstatistica ||
      TIPO_RETORNO_ESTATISTICA_DISCRETO,
    diasUteisPeriodicidadeNumerador:
      medidaSelecionada.diasUteisPeriodicidadeNumerador,
    diasUteisPeriodicidadeVolatilidade:
      medidaSelecionada.diasUteisPeriodicidadeVolatilidade,
    ativos,
    campos,
    compradores,
    idMedidaUsuario,
    ajusteDias: isNotNil(medidaSelecionada.ajusteDias)
      ? medidaSelecionada.ajusteDias
      : getObjectKeyByValue(
          MAP_ID_AJUSTE_DIAS_TIPO_DATA_REFERENCIA_INDICADORES_FINANCEIROS,
          medidaSelecionada.dataReferencia
        ) || ID_AJUSTE_DIAS_NORMAL,
    periodos,
    dataInicial: Maybe(medidaSelecionada.dataInicial)
      .map(moment)
      .getOrElse(null),
    dataFinal: Maybe(medidaSelecionada.dataFinal)
      .map(moment)
      .getOrElse(null),
    dataEspecifica: Maybe(medidaSelecionada.dataEspecifica)
      .map(moment)
      .getOrElse(null),
    anos:
      typeof medidaSelecionada.ano === "number"
        ? [medidaSelecionada.ano]
        : typeof medidaSelecionada.anoReferencia === "number"
        ? [medidaSelecionada.anoReferencia]
        : [],
    tiposQuantidadeDiasReferencia:
      typeof medidaSelecionada.tipoQuantidadeDiasReferencia === "number"
        ? [medidaSelecionada.tipoQuantidadeDiasReferencia]
        : [],
    frequencias:
      typeof medidaSelecionada.frequencia === "number"
        ? [medidaSelecionada.frequencia]
        : typeof medidaSelecionada.trimestreReferencia === "number"
        ? [medidaSelecionada.trimestreReferencia]
        : medidaSelecionada.frequencias || [],
    tipoCalculo:
      modoApp === MODO_APP_SERIES
        ? medidaSelecionada.tipoCalculoAnaliseHorizontal
        : medidaSelecionada.medida.modoExibicao ===
            MODO_EXIBICAO_ACUMULACAO_DEFLATOR_COM_ANALISE_HORIZONTAL &&
          medidaSelecionada.periodo === ID_PERIODO_ANALISE_HORIZONTAL
        ? ID_TIPO_CALCULO_VARIACAO_PERCENTUAL
        : ID_TIPO_CALCULO_PONTUAL,
    ultimaNegociacaoMes: medidaSelecionada.ultimaNegociacao,
    ajustesPreco:
      typeof medidaSelecionada.tipoAjuste === "number"
        ? [medidaSelecionada.tipoAjuste]
        : [],
    fonteFidc: medidaSelecionada.fonteDeDadosFIDC,
    janelaMovel: medidaSelecionada.janelaMovel,
    ...(medidaSelecionada.medida.grupamentoMedida.nome ===
    GRUPAMENTO_MEDIDAS_PERSONALIZADAS
      ? gerarCamposEdicaoMedidaPersonalizada(state, medidaSelecionada)
      : {}),
    escala,
    casasDecimais,
  };

  return reject(isEmpty, edicaoMedida);
}

// Função redefinida pois não podemos importar-la da `medidasSelecionadasSelectors.js`
// por conta de referencia circular
const getMedidasSelecionadas = state => state.medidasSelecionadas;

export const getMedidaSendoEditada: SimpleSelector<?MedidaSelecionada> = createSelector(
  getIndiceMedidaEditada,
  getMedidasSelecionadas,
  (indice, medidas) =>
    Maybe(indice)
      .map(indice => medidas[indice])
      .getOrElse(null)
);

const medidasPorAlias = createSelector(
  getMedidasSelecionadas,
  indexBy(prop("alias"))
);

const dependenciasPorAlias = createSelector(
  medidasPorAlias,
  map(medida =>
    medida.expressao
      ? Maybe(parseiaExpressaoPorAtivo(medida.expressao))
          .map(parseiaExpressao)
          .map(referenciasDaExpressao)
          .map(referencias => new Set(referencias))
          .getOrElse(new Set())
      : new Set()
  )
);

export const getMedidasReferenciadasEmExpressoes = createSelector(
  dependenciasPorAlias,
  compose(
    referencias => new Set(referencias),
    chain(referencias => [...referencias]),
    values
  )
);

export const getPesosRanking = createSelector(
  getEdicaoMedida,
  propOr([], "pesosRanking")
);

export const getColunasRanking = createSelector(
  getEdicaoMedida,
  propOr({}, "colunasRanking")
);

export const getExpressao = createSelector(
  getEdicaoMedida,
  propOr("", "expressao")
);

export const getNomeMedidaComposta = createSelector(
  getEdicaoMedida,
  propOr("", "nome")
);

export const getFormato = createSelector(
  getEdicaoMedida,
  propOr("", "formato")
);

export const getMedidasRankeaveis = createSelector(
  getMedidasSelecionadas,
  getEdicaoMedida,
  dependenciasPorAlias,
  (medidas, medidaEditada, dependenciasPorAlias) =>
    medidas
      .filter(
        medida =>
          medida.medida.grupamentoMedida.nome !==
            GRUPAMENTO_MEDIDAS_QUALITATIVAS &&
          medida.medida.idConfiguracaoRelatorio !==
            ID_CONFIGURACAO_RELATORIO_MEDIDA_MEDIDA_USUARIO
      )
      .filter(medida => !isMedidaNaoNumerica(medida.medida))
      .filter(medida =>
        medidaEditada.alias ? medida.alias !== medidaEditada.alias : true
      )
      .filter(medida => {
        const verificaCiclo = (medida, visitados) => {
          const dependencias = dependenciasPorAlias[medida] || new Set();
          if (dependencias.size > 0) {
            return (
              !dependencias.has(medidaEditada.alias) &&
              [...dependencias].every(dep =>
                visitados.has(dep)
                  ? true
                  : verificaCiclo(dep, new Set([...visitados, dep]))
              )
            );
          } else {
            return true;
          }
        };

        return verificaCiclo(medida.alias, new Set());
      })
);

export const getAtivosCarteiraSelecionados: Selector<
  Array<any>
> = createSelector(getEdicaoMedida, propOr([], "ativos"));

export const getCamposSelecionados: Selector<Array<Id>> = createSelector(
  getEdicaoMedida,
  propOr([], "campos")
);

export const getCamposMedida: Selector<
  Array<{ id: Id, nome: String }>
> = createSelector(
  getMedidaSelecionada,
  getCamposPorMedida,
  (medidaSelecionada, camposPorMedida) =>
    Maybe(medidaSelecionada)
      .chain(medida => Maybe(medida.medidasSecundarias))
      .map(medidas =>
        medidas.map(({ codigoOriginal: id, nome }, ordem) => ({
          id,
          nome: propOr(nome, "nome", camposPorMedida[id]),
          ordem: propOr(ordem, "ordem", camposPorMedida[id]),
        }))
      )
      .map(sortBy(prop("ordem")))
      .map(map(omit(["ordem"])))
      .getOrElse([])
);

export const getCompradoresSelecionados: Selector<number> = createSelector(
  getEdicaoMedida,
  prop("compradores")
);

export const getVisualizacaoCarteiraSelecionada: Selector<string> = createSelector(
  getEdicaoMedida,
  prop("visualizacaoCarteira")
);

export const getRetornoNumeradorSelecionado: Selector<string> = createSelector(
  getEdicaoMedida,
  prop("retornoNumerador")
);

export const getCalculoNumeradorSelecionado: Selector<string> = createSelector(
  getEdicaoMedida,
  prop("calculoNumerador")
);

export const getPeriodicidadeEstatisticaNumeradorSelecionada: Selector<string> = createSelector(
  getEdicaoMedida,
  prop("periodicidadeNumerador")
);

export const getPeriodicidadeEstatisticaVolatilidadeSelecionada: Selector<string> = createSelector(
  getEdicaoMedida,
  prop("periodicidadeVolatilidade")
);

export const getDiasUteisPeriodicidadeNumeradorSelecionado = (
  state: State
): ?number => getEdicaoMedida(state).diasUteisPeriodicidadeNumerador;

export const getDiasUteisPeriodicidadeVolatilidadeSelecionado = (
  state: State
): ?number => getEdicaoMedida(state).diasUteisPeriodicidadeVolatilidade;

export const getTipoVolatilidadeSelecionada: Selector<string> = createSelector(
  getEdicaoMedida,
  prop("tipoVolatilidade")
);

export const getEstimacaoVolatilidadeSelecionada: Selector<string> = createSelector(
  getEdicaoMedida,
  prop("estimacaoVolatilidade")
);

export const getTipoRetornoConfiguracoesEstatisticaSelecionada: Selector<string> = createSelector(
  getEdicaoMedida,
  prop("tipoRetornoConfiguracoesEstatistica")
);

export const getCasasDecimaisSelecionado: Selector<number> = createSelector(
  getEdicaoMedida,
  prop("casasDecimais")
);

const getArrayDeFormatacoesMedidasSecundarias = createSelector(
  getMedidaSelecionada,
  medidaSelecionada => {
    return propIs(Array, "medidasSecundarias", medidaSelecionada)
      ? prop("medidasSecundarias", medidaSelecionada).map(
          medida => medida.formatacao
        )
      : [];
  }
);

// Checa se a medida ou quaisquer de suas medidas secundárias tem formatacão que requer casas decimais com excecão se o tipo das medidas secundárias for PERIODO_SERIE (regra de negócio)
export const isMedidaOuSecundariasComCasasDecimais = createSelector(
  getMedidaSelecionada,
  medidaSelecionada => {
    return (
      medidaSelecionada &&
      !isMultiplasMedidasSelecionadas(medidaSelecionada) &&
      (!MEDIDAS_SEM_CASAS_DECIMAIS.includes(medidaSelecionada.formatacao) ||
        (complement(propEq)(
          "tipoMedidasSecundarias",
          "PERIODO_SERIE",
          medidaSelecionada
        ) &&
          propIs(Array, "medidasSecundarias", medidaSelecionada) &&
          any(complement(includesIn(MEDIDAS_SEM_CASAS_DECIMAIS)))(
            getArrayDeFormatacoesMedidasSecundarias()
          )))
    );
  }
);

export const isMedidaOuSecundariasComEscalaMoeda = createSelector(
  getMedidaSelecionada,
  getTipoCalculoSelecionado,
  (medidaSelecionada, tipoCalculoSelecionado) => {
    const ESCALAS_MOEDA = FORMATACAO_MOEDAS.concat(FORMATACAO_MOEDA_MILHARES);
    return (
      tipoCalculoSelecionado !== ID_TIPO_CALCULO_VARIACAO_PERCENTUAL &&
      !isMultiplasMedidasSelecionadas(medidaSelecionada) &&
      medidaSelecionada &&
      (ESCALAS_MOEDA.includes(medidaSelecionada.formatacao) ||
        (propIs(Array, "medidasSecundarias", medidaSelecionada) &&
          complement(propEq)(
            "tipoMedidasSecundarias",
            "PERIODO_SERIE",
            medidaSelecionada
          ) &&
          any(includesIn(ESCALAS_MOEDA))(
            getArrayDeFormatacoesMedidasSecundarias()
          )))
    );
  }
);
